import { useCreateUser } from "@/api/mutations/users";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Message } from "@/components/ui/message";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

const formSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  role: z.enum(["ADMIN", "AGENT"]),
});

type FormData = z.infer<typeof formSchema>;

export const CreateUserDialog = () => {
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending } = useCreateUser();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
      role: "AGENT",
    },
  });

  const onSubmit = async (data: FormData) => {
    mutateAsync(data)
      .then(() => {
        toast.success("User created");
        setOpen(false);
        reset();
      })
      .catch((err: AxiosError<{ message: string }, unknown>) => {
        toast.error(err.response?.data?.["message"] ?? "Failed to create user");
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button data-testid="open-create-user-dialog">Create User</Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Username</Label>
            <Controller
              name="username"
              control={control}
              render={({ field }) => <Input {...field} placeholder="Username" />}
            />
            {errors.username && <Message variant="error">{errors.username.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Password</Label>
            <Controller
              name="password"
              control={control}
              render={({ field }) => <Input {...field} type="password" placeholder="Password" />}
            />
            {errors.password && <Message variant="error">{errors.password.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.role && <Message variant="error">{errors.role.message}</Message>}
          </div>

          <Button className="w-full" type="submit" disabled={isPending}>
            {isPending ? "Creating..." : "Create"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
